/**
 * @file OrdersHistory.jsx
 * @description Manages display and handling of trading order history
 *
 * @section Responsibilities
 * - Display order history
 * - Handle order data
 * - Process historical data
 * - Manage history state
 * - Handle history errors
 * - Provide history controls
 *
 * @section Dependencies
 * - HistoryService: History management
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - History API: History operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: History controls
 *
 * @section State Management
 * - historyState: History status
 * - dataState: Data status
 * - controlState: Control status
 * - errorState: Error handling
 *
 * @section Events
 * - onHistoryUpdate: History updates
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Array} orders - Order history
 * @prop {Function} onUpdate - Update callback
 * @prop {Object} initialData - Initial history data
 * @prop {Boolean} autoUpdate - Controls auto-updates
 */

import React from "react";
import Widget from "../Widget";

// Order records now come from our own DB (ADFE-142), normalized at write
// time: multi-leg orders carry a `legs` array instead of the single
// symbol/side the old broker-passthrough shape had, so the symbol/side
// columns join across legs rather than reading top-level fields that only
// ever existed for single-leg orders.
const legsSummary = (order) => {
  if (!order.legs?.length) return order.symbol || "-";
  return order.legs.map((leg) => leg.optionSymbol).join(", ");
};

function OrdersHistory({ strategyId, orders }) {
  return (
    <Widget name={"orders-history"} width={"full-width"}>
      <div className="heading">
        <h2 className="left-title">Orders history</h2>
        <table className="basic-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Side</th>
              <th>Qty</th>
              <th>Type</th>
              <th>Dur.</th>
              <th>Filled/Qty</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders?.length > 0 ? (
              <>
                {orders.map((order) => (
                  <tr key={order.localOrderId}>
                    <td className="symbol first">
                      <p className="order-text">{legsSummary(order)}</p>
                    </td>
                    <td className="side">
                      <p className="order-text">{order.netDirection}</p>
                    </td>
                    <td className="qty">
                      <p className="order-text">{order.spreadQuantity}</p>
                    </td>
                    <td className="type">
                      <p className="order-text">{order.orderType}</p>
                    </td>
                    <td className="dur">
                      <p className="order-text">{order.duration}</p>
                    </td>
                    <td className="filled">
                      <p className="order-text">
                        {order.filledSpreadQuantity}/{order.spreadQuantity}
                      </p>
                    </td>
                    <td className="status">
                      <p className="order-text">{order.status}</p>
                    </td>
                  </tr>
                ))}
              </>
            ) : (
              <tr>
                <td colSpan={7}>No orders in the selected range</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Widget>
  );
}

export default OrdersHistory;
